import type { ReactElement } from "react";
import { memo, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { setIsOnboardingWizardOpened } from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import { selectIsOnboardingWizardOpened } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import useSendWizardResultMutation from "app/pages/sales-intelligence/sub-modules/onboarding/query/useSendWizardResultMutation";
import useWizardQuestionsQuery from "app/pages/sales-intelligence/sub-modules/onboarding/query/useWizardQuestionsQuery";
import type { OnboardingWizardAnswer } from "app/pages/sales-intelligence/sub-modules/onboarding/types/OnboardingWizardAnswer.dto";
import { WizardStep } from "app/pages/sales-intelligence/sub-modules/onboarding/types/WizardStepType";
import WizardLoading from "app/pages/sales-intelligence/sub-modules/onboarding/wizard/components/WizardLoading/WizardLoading";
import WizardModalContent from "app/pages/sales-intelligence/sub-modules/onboarding/wizard/components/WizardModalContent/WizardModalContent";
import {
    USER_CLOSED_ONBOARDING,
    USER_COMPLETED_ONBOARDING,
} from "app/pages/sales-intelligence/sub-modules/onboarding/constants/preferences";
import useRedirectionByGoal from "app/pages/sales-intelligence/sub-modules/onboarding/wizard/hooks/useRedirectionByGoal";
import { PreferencesService } from "app/services/preferences/preferencesService";

import WizardModal from "../components/WizardModal/WizardModal";

import { StyledWizardContentWrapper, StyledWizardLeftPartWrapper } from "./styles";
import WizardContentContainer from "./WizardContentContainer";
import WizardLeftPartContainer from "./WizardLeftPartContainer";

type OnboardingPreferences = { userCompletedOnboarding?: boolean; userClosedOnboarding?: boolean };

const WizardContainer = (): ReactElement => {
    const isOnboardingCompleted = PreferencesService.get(USER_COMPLETED_ONBOARDING);
    const isOnboardingClosed = PreferencesService.get(USER_CLOSED_ONBOARDING);

    const [isOpenLocal, setIsOpenLocal] = useState(!isOnboardingCompleted && !isOnboardingClosed);
    const [currentStep, setCurrentStep] = useState<number>(WizardStep.Focus);

    const dispatch = useDispatch();
    const isWizardOpenedFromStore = useSelector(selectIsOnboardingWizardOpened);

    const { isLoading, data, isError } = useWizardQuestionsQuery();
    const { mutate, isPending: isMutationLoading, isSuccess: isMutationSuccess } = useSendWizardResultMutation();

    const { redirect, setGoal } = useRedirectionByGoal();

    const isOpen = isOpenLocal || isWizardOpenedFromStore;

    const completeOnboarding = (isCompleted?: boolean): Promise<OnboardingPreferences | void> => {
        return PreferencesService.add<OnboardingPreferences>({
            [USER_COMPLETED_ONBOARDING]: isOnboardingCompleted || !!isCompleted,
            [USER_CLOSED_ONBOARDING]: true,
        }).catch((err) => {
            Sentry.error("Preferences service: error adding preference (userCompletedOnboarding)", err);
        });
    };

    const closeOnboarding = (shouldRedirect = false): void => {
        setIsOpenLocal(false);
        dispatch(setIsOnboardingWizardOpened(false));
        if (shouldRedirect) {
            redirect();
        }
    };

    const handleClose = () => {
        completeOnboarding();

        closeOnboarding();
    };

    const handleSubmit = (answer: OnboardingWizardAnswer) => {
        setGoal(answer.StartingAction);
        mutate(answer);
    };

    useEffect(() => {
        if (!isMutationSuccess) {
            return;
        }
        completeOnboarding(true).then(() => {
            closeOnboarding(true);
        });
    }, [isMutationSuccess]);

    return (
        <WizardModal
            isOpen={isOpen}
            onClose={handleClose}
        >
            <WizardModalContent isError={isError}>
                <StyledWizardLeftPartWrapper>
                    <WizardLeftPartContainer currentStep={currentStep} />
                </StyledWizardLeftPartWrapper>

                <StyledWizardContentWrapper>
                    <WizardLoading isLoading={isLoading || !data}>
                        <WizardContentContainer
                            isLoading={isMutationLoading}
                            data={data}
                            currentStep={currentStep}
                            onSubmit={handleSubmit}
                            onClose={handleClose}
                            onStepChange={setCurrentStep}
                        />
                    </WizardLoading>
                </StyledWizardContentWrapper>
            </WizardModalContent>
        </WizardModal>
    );
};

export default memo(WizardContainer);
